import { Button } from "@rosc/ui/v32/button";
import { Link } from "@tanstack/react-router";
import { ArrowUpRight, ChevronLeft, CircleAlert, Link2Off } from "lucide-react";
import type { JSX } from "react";
import { createElement, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { PRICING_URL } from "@/pages/Settings/redesign/SubscriptionPanel";

export const PRICING_TABLE_SCRIPT = "https://js.stripe.com/v3/pricing-table.js";
const LOAD_TIMEOUT_MS = 15_000;

export type CheckoutState = "loading" | "ready" | "error" | "noSession";

export interface CheckoutConfig {
  tableId?: string;
  publishableKey?: string;
  clientSecret: string;
}

// Missing table, key or session secret: the link was opened without a
// session (or the build has no Stripe configuration).
export const hasCheckoutSession = (config: CheckoutConfig): boolean =>
  !!config.tableId && !!config.publishableKey && !!config.clientSecret;

// Loads Stripe's pricing table script once and waits for the element.
const usePricingTable = (enabled: boolean): CheckoutState => {
  const [state, setState] = useState<CheckoutState>(
    enabled ? "loading" : "noSession",
  );
  useEffect(() => {
    if (!enabled) return;
    let done = false;
    const finish = (next: CheckoutState): void => {
      if (done) return;
      done = true;
      setState(next);
    };
    const script = document.createElement("script");
    script.src = PRICING_TABLE_SCRIPT;
    script.async = true;
    script.onerror = () => finish("error");
    document.head.appendChild(script);
    void customElements
      .whenDefined("stripe-pricing-table")
      .then(() => finish("ready"));
    const timer = window.setTimeout(() => finish("error"), LOAD_TIMEOUT_MS);
    return () => {
      done = true;
      window.clearTimeout(timer);
      script.remove();
    };
  }, [enabled]);
  return state;
};

const Frame = ({ children }: { children: React.ReactNode }): JSX.Element => {
  const { t } = useTranslation();
  return (
    <div className="v3-remap flex min-h-dvh w-full flex-col bg-v3-canvas font-v3 text-v3-text">
      <header className="mx-auto flex h-14 w-full max-w-5xl shrink-0 items-center gap-2 px-4 sm:px-6">
        <img
          alt=""
          className="size-7"
          src={`${import.meta.env.BASE_URL}images/logos/logo.svg`}
        />
        <span className="flex-1 text-[17px] font-semibold tracking-tight">
          ROSC
        </span>
        <Button asChild variant="ghost">
          <Link hash="subscription" to="/settings">
            <ChevronLeft aria-hidden />
            {t("checkout.back")}
          </Link>
        </Button>
      </header>
      <main className="mx-auto w-full max-w-5xl flex-1 px-4 pb-12 pt-4 sm:px-6 sm:pt-8">
        {children}
      </main>
    </div>
  );
};

const Notice = ({
  icon,
  title,
  description,
  children,
  testId,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  children: React.ReactNode;
  testId: string;
}): JSX.Element => (
  <section
    className="mx-auto mt-6 max-w-md rounded-[18px] bg-v3-panel p-6 text-center shadow-v3-hairline sm:p-8"
    data-testid={testId}
    role="alert"
  >
    <span
      aria-hidden
      className="mx-auto flex size-12 items-center justify-center rounded-2xl bg-v3-surface-2 text-v3-text-2 shadow-v3-hairline [&_svg]:size-6"
    >
      {icon}
    </span>
    <h2 className="mt-4 text-[17px] font-semibold leading-[22px]">{title}</h2>
    <p className="mt-1 text-[14px] leading-relaxed text-v3-text-2">
      {description}
    </p>
    <div className="mt-5 flex flex-col items-stretch justify-center gap-2 sm:flex-row sm:items-center">
      {children}
    </div>
  </section>
);

// /stripe in the v3.2 app: the Stripe pricing table (the existing
// checkout, opened from Settings › Subscription with a customer session)
// on the v3.2 canvas, with loading, error and missing-session states.
export const StripeCheckout = ({
  config,
  forceState,
}: {
  config: CheckoutConfig;
  // Stories: show a state without loading Stripe.
  forceState?: CheckoutState;
}): JSX.Element => {
  const { t } = useTranslation();
  const valid = hasCheckoutSession(config);
  const loaded = usePricingTable(valid && !forceState);
  const state = forceState ?? (valid ? loaded : "noSession");

  return (
    <Frame>
      <h1 className="font-v3-display text-[26px] font-bold leading-8 tracking-[-0.02em] sm:text-[30px] sm:leading-9">
        {t("checkout.title")}
      </h1>
      <p className="mt-1 max-w-2xl text-[14px] leading-relaxed text-v3-text-2">
        {t("checkout.subtitle")}
      </p>
      {state === "noSession" && (
        <Notice
          description={t("checkout.noSession.description")}
          icon={<Link2Off aria-hidden />}
          testId="checkout-no-session"
          title={t("checkout.noSession.title")}
        >
          <Button asChild variant="primary">
            <Link hash="subscription" to="/settings">
              {t("checkout.noSession.action")}
            </Link>
          </Button>
        </Notice>
      )}
      {state === "error" && (
        <Notice
          description={t("checkout.error.description")}
          icon={<CircleAlert aria-hidden />}
          testId="checkout-error"
          title={t("checkout.error.title")}
        >
          <Button variant="primary" onClick={() => window.location.reload()}>
            {t("common.retry")}
          </Button>
          <Button asChild variant="secondary">
            <a href={PRICING_URL} rel="noopener noreferrer" target="_blank">
              {t("settings.subscription.v3.comparePlans")}
              <ArrowUpRight aria-hidden />
            </a>
          </Button>
        </Notice>
      )}
      {state === "loading" && (
        <div
          aria-busy
          aria-label={t("checkout.loading")}
          className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3"
          data-testid="checkout-loading"
          role="status"
        >
          {[0, 1, 2].map((key) => (
            <div
              key={key}
              className="h-[340px] animate-pulse rounded-v3-panel bg-v3-surface-2 max-sm:h-[220px]"
            />
          ))}
        </div>
      )}
      {state === "ready" && (
        <div
          className="mt-6 overflow-hidden rounded-v3-panel bg-v3-panel p-2 shadow-v3-hairline sm:p-4"
          data-testid="checkout-table"
        >
          {createElement("stripe-pricing-table", {
            "pricing-table-id": config.tableId,
            "publishable-key": config.publishableKey,
            "customer-session-client-secret": config.clientSecret,
          })}
        </div>
      )}
    </Frame>
  );
};
